import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { LOGIN_I18N } from "@/lib/landingI18n";
import { LANG_NAMES } from "@/lib/i18n";
import useDocumentLocale from "@/hooks/useDocumentLocale";

export default function Login() {
  const [lang, setLang] = useState(() => {
    const preferred = localStorage.getItem("projevista-lang") || navigator.language?.slice(0, 2);
    return LOGIN_I18N[preferred] ? preferred : "en";
  });
  const t = LOGIN_I18N[lang];
  useDocumentLocale(lang, t.title);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const user = await login(email, password);
      if (user.role === "platform_admin") navigate("/studio");
      else navigate("/portal");
    } catch (e) {
      setErr(t.failed);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#FDFCF8]">
      <div className="hidden md:flex flex-col justify-between flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjB2aWxsYSUyMGFyY2hpdGVjdHVyZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4MjQxODE5OHww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-black/10 to-black/50" />
        <div className="relative z-10 p-12 text-white">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="login-back-home">
            <div className="h-2.5 w-2.5 rounded-full bg-[#C4A47C]" />
            <span className="font-display tracking-tight">Projevista</span>
          </Link>
        </div>
        <div className="relative z-10 p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.3em] opacity-80">{t.studio}</div>
          <div className="font-display text-4xl mt-2 max-w-md leading-tight">{t.intro}</div>
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
          <div className="flex justify-end mb-6">
            <select aria-label={t.language} value={lang} onChange={e => { setLang(e.target.value); localStorage.setItem("projevista-lang", e.target.value); }} className="bg-transparent border border-zinc-300 rounded px-2 py-1" data-testid="login-language">
              {Object.keys(LOGIN_I18N).map(l => <option key={l} value={l}>{LANG_NAMES[l]}</option>)}
            </select>
          </div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">{t.sign_in}</div>
          <h1 className="font-display text-3xl tracking-tight mb-8">{t.welcome}</h1>

          <label htmlFor="login-email" className="block text-xs font-medium text-zinc-700 mb-2">{t.email}</label>
          <input
            id="login-email" autoComplete="username" dir="ltr"
            data-testid="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-zinc-300 bg-white outline-none focus:border-zinc-900 transition mb-5"
            required
          />

          <label htmlFor="login-password" className="block text-xs font-medium text-zinc-700 mb-2">{t.password}</label>
          <input
            id="login-password" autoComplete="current-password"
            data-testid="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-zinc-300 bg-white outline-none focus:border-zinc-900 transition mb-2"
            required
          />

          {err && (
            <div role="alert" className="text-sm text-red-600 mt-3" data-testid="login-error">{err}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-zinc-900 text-white text-sm hover:bg-zinc-700 transition disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t.sign_in}
          </button>
        </form>
      </div>
    </div>
  );
}
